import React, { useState, useEffect, useRef, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, RefreshCw } from 'lucide-react';
import { buildNavMenus, resolveHomePath, isRepOnlyPortalUser, NavMenu } from '../config/navigation';
import { getStoredRepresentativeId, getStoredRoleName, getUserPermissions, hasPermission } from '../api/auth';
import { AUTH_USER_CHANGED_EVENT } from '../constants/auth-events';
import { useViewport } from '../hooks/useViewport';
import { usePortalMainNavFlag } from '../features/client-portal/usePortalMainNav';

const ORDERS_EXTRA_PATHS = ['/unified-orders'];

function isMenuActive(menu: NavMenu, pathname: string): boolean {
  if (menu.to) {
    return pathname === menu.to || pathname.startsWith(`${menu.to}/`);
  }
  if (menu.label === 'Заказы') {
    return (
      (menu.items?.some(item => item.to && pathname.startsWith(item.to)) ?? false) ||
      ORDERS_EXTRA_PATHS.some(p => pathname === p || pathname.startsWith(`${p}/`))
    );
  }
  if (menu.items) {
    return menu.items.some(
      item => item.to && (pathname === item.to || pathname.startsWith(`${item.to}/`)),
    );
  }
  if (menu.groups) {
    return menu.groups.some(g =>
      g.items.some(item => item.to && pathname.startsWith(item.to)),
    );
  }
  return false;
}

export function SubNav() {
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { isTablet, isDesktop } = useViewport();
  const portalMain = usePortalMainNavFlag();
  const [navMenus, setNavMenus] = useState<NavMenu[]>(() =>
    buildNavMenus(getUserPermissions(), code => hasPermission(code), {
      representativeId: getStoredRepresentativeId(),
      roleName: getStoredRoleName(),
    }),
  );

  const refreshMenus = useCallback(() => {
    setNavMenus(buildNavMenus(getUserPermissions(), code => hasPermission(code), {
      representativeId: getStoredRepresentativeId(),
      roleName: getStoredRoleName(),
      portalMain,
    }));
  }, [portalMain]);

  useEffect(() => {
    refreshMenus();
    window.addEventListener(AUTH_USER_CHANGED_EVENT, refreshMenus);
    return () => window.removeEventListener(AUTH_USER_CHANGED_EVENT, refreshMenus);
  }, [refreshMenus]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(e.target as Node)) {
        setOpenMenu(null);
        setOpenGroup(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    setOpenMenu(null);
    setOpenGroup(null);
  }, [location.pathname]);

  const accordionGroups = !isDesktop; // tablet: accordion instead of wide grid
  const homeTo = resolveHomePath(getUserPermissions(), undefined, getStoredRoleName());

  return (
    <nav className={`subnav${isTablet ? ' subnav--tablet' : ''}`} ref={navRef} data-testid="subnav">
      <div className="subnav__inner">
        {!isRepOnlyPortalUser(getUserPermissions()) && <><NavLink
          to={homeTo}
          className={({ isActive }) => `subnav__item${isActive ? ' subnav__item--active' : ''}`}
        >
          Главная
        </NavLink>

        <button
          className="subnav__item subnav__item--action"
          onClick={() => window.location.reload()}
          title="Обновить"
          type="button"
        >
          <RefreshCw size={12} />
        </button>

        <div className="subnav__sep" /></>}

        {navMenus.map(menu => {
          const active = isMenuActive(menu, location.pathname);
          const open = openMenu === menu.label;

          if (menu.to && !menu.items && !menu.groups) {
            return (
              <NavLink
                key={menu.label}
                to={menu.to}
                className={({ isActive }) => `subnav__item${isActive ? ' subnav__item--active' : ''}`}
              >
                {menu.label}
              </NavLink>
            );
          }

          return (
            <div key={menu.label} className="subnav__menu">
              <button
                type="button"
                className={`subnav__item subnav__item--dropdown${active ? ' subnav__item--active' : ''}${open ? ' subnav__item--open' : ''}`}
                onClick={() => setOpenMenu(open ? null : menu.label)}
                onMouseEnter={() => (openMenu !== null ? setOpenMenu(menu.label) : undefined)}
              >
                {menu.label}
                <ChevronDown size={11} className={`subnav__chevron${open ? ' subnav__chevron--open' : ''}`} />
              </button>

              {open && (
                <div
                  className={`subnav__dropdown${
                    menu.groups
                      ? accordionGroups
                        ? ' subnav__dropdown--accordion'
                        : ' subnav__dropdown--wide'
                      : ''
                  }`}
                >
                  {menu.items?.map((item, i) =>
                    item.sep ? (
                      <div key={i} className="subnav__dropdown-sep" />
                    ) : item.to ? (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        className={({ isActive }) =>
                          `subnav__dropdown-item${isActive ? ' subnav__dropdown-item--active' : ''}`
                        }
                      >
                        {item.label}
                      </NavLink>
                    ) : item.action === 'refresh' ? (
                      <button
                        key={i}
                        type="button"
                        className="subnav__dropdown-item"
                        onClick={() => {
                          setOpenMenu(null);
                          window.location.reload();
                        }}
                      >
                        {item.label}
                      </button>
                    ) : null,
                  )}

                  {menu.groups && accordionGroups && (
                    <div className="subnav__accordion">
                      {menu.groups.map(group => {
                        const gOpen = openGroup === group.groupLabel;
                        return (
                          <div key={group.groupLabel} className="subnav__accordion-group">
                            <button
                              type="button"
                              className={`subnav__group-header${gOpen ? ' subnav__group-header--open' : ''}`}
                              onClick={() => setOpenGroup(gOpen ? null : group.groupLabel)}
                            >
                              <span className="subnav__group-icon">{group.icon}</span>
                              <span>{group.groupLabel}</span>
                              <ChevronDown size={12} className={`subnav__chevron${gOpen ? ' subnav__chevron--open' : ''}`} />
                            </button>
                            {gOpen && (
                              <div className="subnav__group-items">
                                {group.items.map(item =>
                                  item.to ? (
                                    <NavLink
                                      key={item.to}
                                      to={item.to}
                                      className={({ isActive }) =>
                                        `subnav__dropdown-item${isActive ? ' subnav__dropdown-item--active' : ''}`
                                      }
                                    >
                                      {item.label}
                                    </NavLink>
                                  ) : null,
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {menu.groups && !accordionGroups && (
                    <div className="subnav__group-grid">
                      {menu.groups.map(group => (
                        <div key={group.groupLabel} className="subnav__group">
                          <div className="subnav__group-header subnav__group-header--static">
                            <span className="subnav__group-icon">{group.icon}</span>
                            <span>{group.groupLabel}</span>
                          </div>
                          <div className="subnav__group-items">
                            {group.items.map(item =>
                              item.to ? (
                                <NavLink
                                  key={item.to}
                                  to={item.to}
                                  className={({ isActive }) =>
                                    `subnav__dropdown-item${isActive ? ' subnav__dropdown-item--active' : ''}`
                                  }
                                >
                                  {item.label}
                                </NavLink>
                              ) : null,
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
}
